import { User } from "@/features/system/types";

import { getAnalyticsUserRole } from "@/utils/getAnalyticsUserRole";
import { hashEmailIdentity } from "@/utils/hashIdentity";

type MixpanelClient = {
  identify: (identity: string) => void;
  reset: () => void;
  track: (
    eventName: string,
    properties?: Record<string, string | number | boolean>,
  ) => void;
  people: {
    set: (properties: Record<string, string>) => void;
  };
};

export const MixpanelEventNames = {
  viewByPlaceSearchEngaged: "View By Place Search Engaged",
  viewByPlaceSelected: "View By Place Selected",
  viewByPlaceAdvancedSearchOpened: "View By Place Advanced Search Opened",
} as const;

function getMixpanel(): MixpanelClient | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return (window as Window & { mixpanel?: MixpanelClient }).mixpanel;
}

/** Invalidates in-flight identify callbacks after reset or a newer identify. */
let identifyGeneration = 0;

export function identifyMixpanelUser(currentUser: User) {
  const mixpanel = getMixpanel();
  if (!mixpanel || !currentUser.email) {
    return;
  }

  const email = currentUser.email;
  const generation = ++identifyGeneration;

  // SHA-256-hash the identity client-side before sending it, like Heap's identifyHashed.
  void hashEmailIdentity(email).then((hashedIdentity) => {
    if (generation !== identifyGeneration) {
      return;
    }

    mixpanel.identify(hashedIdentity);
    mixpanel.people.set({
      role: getAnalyticsUserRole(email),
      company: currentUser.orgName,
    });
  });
}

export function resetMixpanelIdentity() {
  identifyGeneration += 1;
  getMixpanel()?.reset();
}

export function trackMixpanelEvent(
  eventName: string,
  properties?: Record<string, string | number | boolean>,
) {
  getMixpanel()?.track(eventName, properties);
}
